import { Link } from 'react-router-dom';
import { MoreVertical, type LucideIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Employee } from '@/client/types/hr';

/** Employee name as a link to their profile — falls back to plain text if the employee can't be resolved. */
export function EmployeeLink({ employee, className }: { employee: Employee | null | undefined; className?: string }) {
  if (!employee) return <span className={cn('text-gray-500', className)}>Unknown</span>;
  return (
    <Link
      to={`/admin/human-resource/people/directory/${employee.id}`}
      onClick={(e) => e.stopPropagation()}
      className={cn('font-medium text-gray-900 hover:text-blue-700 hover:underline', className)}
    >
      {employee.name}
    </Link>
  );
}

export function StatusBadge({ label, className }: { label: string; className: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', className)}>
      {label}
    </span>
  );
}

export function EmployeeAvatar({
  employee,
  size = 'md',
}: {
  employee: Pick<Employee, 'name' | 'avatarColor'> | null | undefined;
  size?: 'sm' | 'md';
}) {
  const box = size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-[11px]';
  if (!employee) {
    return <div className={cn('flex shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500', box)}>?</div>;
  }
  return (
    <div
      title={employee.name}
      className={cn('flex shrink-0 items-center justify-center rounded-full font-semibold text-white', box, employee.avatarColor)}
    >
      {employee.name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()}
    </div>
  );
}

export function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-white py-14 text-center text-sm text-gray-400">
      {label}
    </div>
  );
}

export function DeleteConfirmDialog({
  open,
  title,
  description,
  onOpenChange,
  onConfirm,
  actionLabel = 'Delete',
  danger = true,
}: {
  open: boolean;
  title: string;
  description: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  actionLabel?: string;
  danger?: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className={danger ? 'bg-red-600 hover:bg-red-700' : undefined}>
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Panel({
  title,
  action,
  children,
  className = '',
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl border border-gray-100 bg-white p-4 shadow-sm', className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export function ProgressBar({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-gray-100', className)}>
      <div
        className={cn('h-full rounded-full', value >= 100 ? 'bg-emerald-500' : value >= 50 ? 'bg-blue-500' : 'bg-amber-500')}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

export interface RowMenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  hidden?: boolean;
}

/** The dotted per-row action menu. Anything that changes state should open a
 *  confirmation from `onSelect` rather than act directly. */
export function RowMenu({ items, label = 'Actions' }: { items: RowMenuItem[]; label?: string }) {
  const visible = items.filter((i) => !i.hidden);
  if (!visible.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        onClick={(e) => e.stopPropagation()}
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        aria-label={label}
      >
        <MoreVertical className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {visible.map(({ label: itemLabel, icon: Icon, onSelect, danger }) => (
          <DropdownMenuItem
            key={itemLabel}
            onSelect={onSelect}
            className={cn('gap-2', danger && 'text-red-600 focus:text-red-700')}
          >
            <Icon className="h-4 w-4" />
            {itemLabel}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
